import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabaseClient';

export function useChatUnreadCount(userId: string | null) {
  const [unreadCount, setUnreadCount] = useState(0);
  const subscribedRef = useRef(false);

  const fetchUnread = useCallback(async () => {
    if (!userId) {
      setUnreadCount(0);
      return;
    }

    try {
      // 수락된 매칭 ID 목록 가져오기
      const { data: matches } = await supabase
        .from('match_requests')
        .select('id')
        .eq('status', 'accepted')
        .or(`requester_id.eq.${userId},receiver_id.eq.${userId}`);

      if (!matches || matches.length === 0) {
        setUnreadCount(0);
        return;
      }

      const matchIds = matches.map((m) => m.id);

      // 안읽은 메시지 수
      const { count } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .in('match_id', matchIds)
        .eq('is_read', false)
        .neq('sender_id', userId);

      setUnreadCount(count || 0);
    } catch {
      // 조용히 실패
    }
  }, [userId]);

  useEffect(() => {
    fetchUnread();
  }, [fetchUnread]);

  // 실시간 업데이트
  useEffect(() => {
    if (!userId) return;
    if (subscribedRef.current) return;
    subscribedRef.current = true;

    const channel = supabase
      .channel('chat-unread-badge')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        () => { fetchUnread(); }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'messages' },
        () => { fetchUnread(); }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
      subscribedRef.current = false;
    };
  }, [userId, fetchUnread]);

  return unreadCount;
}